import React from 'react';
import { useTranslation } from 'react-i18next';

const FinopsMetricsSummary = ({ costAnomalyDetected, estimatedSavingsUsd, recommendations = [] }) => {
  const { t, i18n } = useTranslation();

  const formatCurrency = (amount, currency = 'USD', locale = 'en') => {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(amount);
  };

  const formatNumber = (num, locale = 'en') => {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(num);
  };

  const totalCarbonFootprint = recommendations.reduce((sum, r) => sum + (r.carbon_footprint_gco2e || 0), 0);
  const totalCarbonReduction = recommendations.reduce((sum, r) => sum + (r.carbon_reduction_potential_gco2e || 0), 0);
  const hasWattScale = totalCarbonFootprint > 0 || totalCarbonReduction > 0;

  const containerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '20px',
    marginBottom: '30px'
  };

  const cardStyle = {
    background: 'rgba(15, 23, 42, 0.65)',
    border: '1px solid rgba(79, 70, 229, 0.28)',
    borderRadius: '16px',
    padding: '20px 24px',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '120px',
    minWidth: 0
  };

  // Badge styles
  const anomalyBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 18px',
    borderRadius: '999px',
    fontSize: '0.9rem',
    fontWeight: '700',
    marginTop: '10px',
    letterSpacing: '0.03em',
    background: costAnomalyDetected ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)',
    color: costAnomalyDetected ? '#FCA5A5' : '#A7F3D0',
    border: `1px solid ${costAnomalyDetected ? 'rgba(239, 68, 68, 0.4)' : 'rgba(16, 185, 129, 0.4)'}`
  };

  return (
    <div>
      {hasWattScale && (
        <div style={{ textAlign: 'left', marginBottom: '12px' }}>
          <h4 className="text-xs font-bold text-indigo-400 tracking-widest uppercase m-0">
            📊 {t('finops.cost_metrics')}
          </h4>
        </div>
      )}
      
      <div style={{ ...containerStyle, marginBottom: hasWattScale ? '20px' : '30px' }}>
        {/* Cost Anomaly Badge Card */}
        <div style={cardStyle}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 4px 0', fontWeight: '600' }}>
            {t('finops.metrics_anomaly_label')}
          </p>
          <div style={anomalyBadgeStyle}>
            <span style={{ marginRight: '6px' }}>{costAnomalyDetected ? '⚠️' : '🛡️'}</span>
            {costAnomalyDetected ? t('finops.metrics_anomaly_detected') : t('finops.metrics_no_anomaly')}
          </div>
        </div>

        {/* Estimated Savings Card */}
        <div style={cardStyle}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 6px 0', fontWeight: '600' }}>
            {t('finops.metrics_savings_label')}
          </p>
          <p style={{ fontSize: '2.25rem', fontWeight: '800', margin: '0', color: '#34D399', letterSpacing: '-0.02em' }}>
            {formatCurrency(estimatedSavingsUsd || 0, 'USD', i18n.language)}
          </p>
        </div>
      </div>

      {hasWattScale && (
        <>
          <div style={{ textAlign: 'left', marginBottom: '12px', marginTop: '10px' }}>
            <h4 className="text-xs font-bold text-green-400 tracking-widest uppercase m-0">
              🍃 {t('finops.metabolic_metrics')}
            </h4>
          </div>
          <div style={containerStyle}>
            {/* Carbon Footprint Card */}
            <div style={{ ...cardStyle, border: '1px solid rgba(16, 185, 129, 0.28)' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 6px 0', fontWeight: '600' }}>
                {t('finops.carbon_footprint')}
              </p>
              <p style={{ fontSize: '2.25rem', fontWeight: '800', margin: '0', color: '#10B981', letterSpacing: '-0.02em' }}>
                {formatNumber(totalCarbonFootprint, i18n.language)} <span style={{ fontSize: '0.95rem', fontWeight: '600', color: 'var(--text-secondary)' }}>gCO2e</span>
              </p>
            </div>

            {/* Carbon Reduction Potential Card */}
            <div style={{ ...cardStyle, border: '1px solid rgba(16, 185, 129, 0.28)' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 6px 0', fontWeight: '600' }}>
                {t('finops.carbon_reduction_potential')}
              </p>
              <p style={{ fontSize: '2.25rem', fontWeight: '800', margin: '0', color: '#34D399', letterSpacing: '-0.02em' }}>
                {formatNumber(totalCarbonReduction, i18n.language)} <span style={{ fontSize: '0.95rem', fontWeight: '600', color: 'var(--text-secondary)' }}>gCO2e</span>
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FinopsMetricsSummary;
